import { Link } from "@tanstack/react-router"
import { Lock, RotateCw } from "lucide-react"
import { type ReactNode, useState } from "react"
import { HairlineTable } from "@/components/archetypes/HairlineTable"
import { Mono } from "@/components/copy-button"
import { PageHeader } from "@/components/shell/page-header"
import { EnvDot } from "@/components/shell/scope-switcher"
import { ErrorState } from "@/components/states"
import { Button } from "@/components/ui/button"
import { PageHeader as ContentHeader } from "@/components/ui/page-header"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import { Skeleton } from "@/components/ui/skeleton"
import { StatStrip } from "@/components/ui/stat-strip"
import { kindLabel } from "@/lib/defs"
import { useFormatNumber, useT } from "@/lib/i18n"
import { useDefinitions } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { useCell } from "@/lib/session"
import { useOntology } from "@/workspace/hooks"
import { HowItWorks } from "@/workspace/how-it-works"
import { shortHash } from "@/lib/utils"

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 py-2 sm:grid-cols-[148px_1fr] sm:items-center sm:gap-4">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-sm [overflow-wrap:anywhere]">{children}</dd>
    </div>
  )
}

export function OverviewPage() {
  const t = useT()
  const formatNumber = useFormatNumber()
  const scope = useScope()
  const { connection } = useCell()
  const defs = useDefinitions(scope)
  return (
    <>
      <PageHeader
        crumbs={[
          <span key="s" className="flex items-center gap-1.5">
            <EnvDot environmentId={scope.environmentId} />
            {scope.orgId} / {scope.environmentId}
          </span>,
        ]}
        actions={
          <>
          <HowItWorks lines={["ws.how.builder"]} />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void defs.refetch()}
            disabled={defs.isFetching}
            aria-label={t("common.refresh")}
          >
            <RotateCw className={defs.isFetching ? "animate-spin" : undefined} /> {t("common.refresh")}
          </Button>
          </>
        }
      />
      <div className="flex-1 overflow-y-auto">
        <Screen width="wide" className="px-4 py-6 md:px-6">
          <h1 className="sr-only">{t("overview.title")}</h1>
          <ContentHeader
            description={t("overview.description", { orgId: scope.orgId, environmentId: scope.environmentId })}
          />
          {defs.isPending ? (
            <div className="mt-6 space-y-3">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-4 w-full max-w-md" />
              ))}
            </div>
          ) : defs.isError ? (
            <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} inline />
          ) : (
            <>
              <StatStrip
                items={[
                  { label: t("overview.setVersion"), value: <span className="font-mono">v{defs.data.version}</span> },
                  { label: t("overview.registryBase"), value: formatNumber(defs.data.registry.length) },
                  {
                    label: t("overview.checkerFindings"),
                    value: formatNumber(defs.data.diagnosticsCount),
                    delta: {
                      value: defs.data.diagnosticsCount
                        ? t("overview.nonError", { count: defs.data.diagnosticsCount })
                        : t("common.none"),
                      tone: "neutral",
                    },
                  },
                  {
                    label: t("overview.unreadableEntries"),
                    value: (
                      <span className={defs.data.unreadable ? "text-warning" : undefined}>
                        {formatNumber(defs.data.unreadable)}
                      </span>
                    ),
                  },
                ]}
              />
              <Section title={t("overview.cell")}>
                <dl className="divide-y divide-border-subtle rounded-lg border border-border bg-card px-5 py-2">
                  <Fact label={t("overview.cell")}>
                    <Mono value={connection.baseUrl} />
                  </Fact>
                  <Fact label={t("overview.receipt")}>
                    <Mono value={defs.data.receiptSha256} display={shortHash(defs.data.receiptSha256, 16)} />
                  </Fact>
                  <Fact label={t("overview.registryBase")}>
                    {defs.data.registry.length === 0 ? (
                      <span className="text-muted-foreground">{t("overview.noneSelected")}</span>
                    ) : (
                      <span className="flex flex-wrap gap-1.5">
                        {defs.data.registry.map((r) => (
                          <code
                            key={r}
                            className="inline-flex items-center gap-1 rounded-sm bg-raised px-1.5 py-0.5 font-mono text-xs"
                          >
                            <Lock className="size-3 text-subtle-foreground" aria-hidden />
                            {r}
                          </code>
                        ))}
                      </span>
                    )}
                  </Fact>
                </dl>
              </Section>

              <Section title={t("overview.byKind")}>
                <HairlineTable
                  rows={[...defs.data.kinds]}
                  getRowKey={(row) => row.kind}
                  minWidthClassName="min-w-[480px]"
                  columns={[
                    {
                      key: "kind",
                      header: t("overview.kind"),
                      cell: (row) => (
                        <>
                          <Link
                            to="/$orgId/$environmentId/definitions/$kind"
                            params={{ ...scope, kind: row.kind }}
                            className="hover:text-primary"
                          >
                            {kindLabel(row.kind, t)}
                          </Link>
                          <code className="ms-2 font-mono text-2xs text-subtle-foreground">{row.kind}</code>
                        </>
                      ),
                    },
                    {
                      key: "tenant",
                      header: t("common.tenant"),
                      align: "end",
                      cell: (row) => formatNumber(row.tenant),
                    },
                    {
                      key: "registry",
                      header: t("common.registry"),
                      align: "end",
                      cell: (row) => (
                        <span className="text-muted-foreground">{formatNumber(row.total - row.tenant)}</span>
                      ),
                    },
                    { key: "total", header: t("overview.total"), align: "end", cell: (row) => formatNumber(row.total) },
                  ]}
                />
              </Section>
              <DisplayGaps />
            </>
          )}
        </Screen>
      </div>
    </>
  )
}

/** Where the workspace had to fall back on humanized identifiers: metadata worth declaring. */
function DisplayGaps() {
  const t = useT()
  const scope = useScope()
  const { ontology } = useOntology(scope)
  const [open, setOpen] = useState(false)
  if (!ontology?.gaps.length) return null
  return (
    <Section title={t("overview.displayGaps")}>
      <p className="text-sm text-muted-foreground">{t("overview.displayGapsHelp", { count: ontology.gaps.length })}</p>
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
        {open ? t("ws.related.showFewer") : t("ws.related.showAll", { count: ontology.gaps.length })}
      </button>
      {open ? (
        <ul className="divide-y divide-border-subtle rounded-lg border border-border bg-card px-4 py-1 font-mono text-2xs text-muted-foreground">
          {ontology.gaps.map((gap) => (
            <li key={gap} className="py-1.5" dir="ltr">
              {gap}
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  )
}
